/**
 * AdminQuickStats — the 4 colourful tiles at the bottom of the Admin
 * Dashboard. Each tile deep-links into its dedicated breakdown page.
 *
 * Extracted from AdminDashboard.jsx (Jun 4 2026) to keep the parent file
 * under control. Self-contained: only needs the counts + a navigate fn.
 */
import React from 'react';
import { Card, CardContent } from './ui/card';

const Tile = ({ to, gradient, ring, label, value, navigate, testId, title }) => (
  <button
    type="button"
    onClick={() => navigate(to)}
    className={`block text-left focus:outline-none focus-visible:ring-2 ${ring} rounded-xl`}
    data-testid={testId}
    title={title}
  >
    <Card className={`bg-gradient-to-br ${gradient} border-0 transition cursor-pointer`}>
      <CardContent className="p-6 text-center">
        <p className="text-white/80 text-sm mb-1">{label}</p>
        <p className="text-3xl font-bold text-white tabular-nums">{value}</p>
        <p className="text-[10px] uppercase tracking-widest font-bold text-white/70 mt-2">
          Tap for breakdown →
        </p>
      </CardContent>
    </Card>
  </button>
);

const AdminQuickStats = ({ users = [], content = [], pendingContent = [], navigate }) => {
  const scribes = users.filter((u) => u.role === 'writer').length;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8" data-testid="admin-quick-stats">
      <Tile
        to="/admin/breakdown/users"
        gradient="from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600"
        ring="focus-visible:ring-blue-300"
        label="Total Users"
        value={users.length}
        navigate={navigate}
        testId="quick-stat-total-users"
        title="View every account that's signed up — opens the full Users list."
      />
      <Tile
        to="/admin/breakdown/content"
        gradient="from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600"
        ring="focus-visible:ring-purple-300"
        label="Total Content"
        value={content.length}
        navigate={navigate}
        testId="quick-stat-total-content"
        title="Every approved + pending piece of content on the platform."
      />
      <Tile
        to="/admin/breakdown/writers"
        gradient="from-green-600 to-green-700 hover:from-green-500 hover:to-green-600"
        ring="focus-visible:ring-green-300"
        label="Active Scribes"
        value={scribes}
        navigate={navigate}
        testId="quick-stat-active-writers"
        title="Users with role = writer — opens the scribes list."
      />
      <Tile
        to="/admin/breakdown/pending"
        gradient="from-orange-600 to-orange-700 hover:from-orange-500 hover:to-orange-600"
        ring="focus-visible:ring-orange-300"
        label="Retain Pending"
        value={pendingContent.length}
        navigate={navigate}
        testId="quick-stat-pending"
        title="Content awaiting your approval."
      />
    </div>
  );
};

export default AdminQuickStats;
